import RtkQueryService from '@lok/services/RtkQueryService';
import { updateUniversity } from '@lok/store/auth/userSlice';

import { inlineErrorResponse, transformErrorResponse } from './api';

const universityService = RtkQueryService.injectEndpoints({
  endpoints: (builder) => ({
    patchUniversity: builder.mutation({
      query: ({ id, data }) => ({
        url: `/v1/university/${id}/`,
        method: 'PATCH',
        data
      }),
      invalidatesTags: ['University', 'UniversityAdminsById'],
      transformErrorResponse,
      // Mirror the saved fields back into the auth-slice user.university so
      // BrandingProvider re-applies CSS vars and the Branding settings page
      // sees the new values immediately. Without this the patched URLs
      // wouldn't appear until the next /me fetch on login.
      // Only mirror when the patched university is the logged-in user's own —
      // super admins editing another client must not have their own session
      // state overwritten with that client's branding.
      async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const myUniId = getState().auth?.user?.university?.id;
          if (
            data &&
            typeof data === 'object' &&
            data.id != null &&
            data.id === myUniId
          ) {
            dispatch(updateUniversity(data));
          }
        } catch {
          // Errors surface via the mutation's normal error channel.
        }
      }
    }),
    fetchUniversities: builder.query({
      query: ({ limit = 100, ordering = 'name' } = {}) => ({
        url: '/v1/university/',
        params: { limit, ordering }
      }),
      providesTags: ['University'],
      transformErrorResponse
    }),
    fetchUniversity: builder.query({
      query: (id) => ({ url: `/v1/university/${id}/` }),
      providesTags: (result, error, id) => [{ type: 'University', id }],
      transformErrorResponse
    }),
    createUniversity: builder.mutation({
      query: (data) => ({
        url: '/v1/university/',
        method: 'POST',
        data
      }),
      invalidatesTags: ['University'],
      transformErrorResponse
    }),
    deleteUniversity: builder.mutation({
      query: (id) => ({
        url: `/v1/university/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: ['University'],
      transformErrorResponse
    }),
    // Super admin: a fresh password for someone whose email never arrived
    // (Dane 2026-10-27). The response carries it once; nothing caches it.
    setTemporaryPassword: builder.mutation({
      query: (userId) => ({ url: `/v1/users/${userId}/temporary-password/`, method: 'POST' }),
      transformErrorResponse
    }),
    listUniversityAdmins: builder.query({
      query: ({ universityId, limit = 100 } = {}) => ({
        url: '/v1/university-admin/',
        params: { university: universityId, limit, expand: 'user' }
      }),
      providesTags: (result, error, arg) => [
        { type: 'UniversityAdminsById', id: arg?.universityId || 'all' }
      ],
      transformErrorResponse
    }),
    // Create net-new admins (or attach existing ones) from raw email + name.
    // The backend get_or_creates the User, attaches a UniversityAdmin, and
    // sends the AdminAccountCreated welcome email. Used by the "Create new
    // admin" path when the existing-user search returns no match.
    createUniversityAdminsBulk: builder.mutation({
      query: ({ universityId, admins }) => ({
        url: `/v1/university/${universityId}/bulk_create/`,
        method: 'POST',
        data: admins // array of { email, first_name, last_name }
      }),
      // General tag on purpose (CON-243 round 3): the settings page's list
      // provides the plain 'UniversityAdminsById' tag, which a per-id
      // invalidation does not reach, so its table sat stale until a manual
      // refresh. Adding an admin who already administers other
      // organizations changes what those lists show too. A general
      // invalidation refetches every provider of the type, per-id ones
      // included, so every surface stays fresh.
      invalidatesTags: ['UniversityAdminsById'],
      transformErrorResponse
    }),
    deleteUniversityAdmin: builder.mutation({
      // This is the ONE definition of this endpoint (CON-243): the settings
      // page's service used to inject a second endpoint under the same name
      // taking a bare id, injectEndpoints silently kept this one, and every
      // settings delete went out as /university-admin/undefined/.
      query: ({ id }) => ({
        url: `/v1/university-admin/${id}/`,
        method: 'DELETE'
      }),
      // General tag on purpose (CON-243 round 3): the settings list provides
      // the plain tag that a per-id invalidation does not reach (Dane's list
      // sat stale until a manual refresh). A general invalidation refetches
      // every provider of the type, the super-admin per-id lists included.
      // Since 2026-09-09 this removes only the one adminship; the account
      // stays (backend #326).
      invalidatesTags: ['UniversityAdminsById'],
      // A 404 here means the row is already gone (double click, another tab);
      // that belongs in a toast, not a hard redirect to /404.
      transformErrorResponse: inlineErrorResponse
    })
  })
});

export const {
  usePatchUniversityMutation,
  useFetchUniversitiesQuery,
  useFetchUniversityQuery,
  useCreateUniversityMutation,
  useDeleteUniversityMutation,
  useListUniversityAdminsQuery,
  useSetTemporaryPasswordMutation,
  useCreateUniversityAdminsBulkMutation,
  useDeleteUniversityAdminMutation
} = universityService;
